import type { ReactNode } from 'react';
import { Navigate } from 'react-router';
import { useConfig, useFeatureFlags } from '@/hooks/useConfig';
import { useSession } from '@/hooks/useSession';

/**
 * Route wall for dev-only screens. The nav item is hidden by the same flag
 * (nav.ts `flag: 'devOnly'`); this is for anyone who types the URL on alpha
 * or production, who is sent to the timesheet as if the route did not exist.
 * Waits for the config request so alpha never flashes the screen first.
 */
export function DevOnly({ children }: { children: ReactNode }) {
  const config = useConfig();
  const flags = useFeatureFlags();
  if (!import.meta.env.DEV && config.isPending) return null;
  if (!flags.devOnly) return <Navigate to="/timesheet" replace />;
  return <>{children}</>;
}

/**
 * Route wall for a feature flag (nav.ts `feature`), for anyone who types the
 * URL while the flag is off for them. The server 404s the feature's API too.
 */
export function FeatureGate({ flag, children }: { flag: string; children: ReactNode }) {
  const session = useSession();
  if (!session.flags[flag]) return <Navigate to="/timesheet" replace />;
  return <>{children}</>;
}
